import { Suspense, lazy, useEffect, useMemo } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntApp, ConfigProvider, Spin } from 'antd';
import esES from 'antd/locale/es_ES';
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useThemeStore } from '@/stores/themeStore';
import { buildAntdTheme } from '@/theme/antd-tokens';

import { AuthGuard, GuestGuard } from '@/auth/AuthGuard';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MainLayout } from '@/layouts/MainLayout';
// Login y 404 quedan eager: son livianas y hacen al primer render.
import { LoginPage } from '@/pages/auth/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Paginas lazy: cada una va a su propio chunk y se descarga al navegar.
// (code-splitting para mantener el bundle inicial chico y sin warnings de Vite)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const EnterprisesPage = lazy(() =>
  import('@/pages/admin/enterprises/EnterprisesPage').then((m) => ({ default: m.EnterprisesPage })));
const UsersPage = lazy(() =>
  import('@/pages/admin/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const MdmsPage = lazy(() =>
  import('@/pages/admin/mdms/MdmsPage').then((m) => ({ default: m.MdmsPage })));
const AppsPage = lazy(() =>
  import('@/pages/apps/AppsPage').then((m) => ({ default: m.AppsPage })));
const ProfilesPage = lazy(() =>
  import('@/pages/profiles/ProfilesPage').then((m) => ({ default: m.ProfilesPage })));
const DevicesPage = lazy(() =>
  import('@/pages/devices/DevicesPage').then((m) => ({ default: m.DevicesPage })));
const DeviceDetailPage = lazy(() =>
  import('@/pages/devices/detail/DeviceDetailPage').then((m) => ({ default: m.DeviceDetailPage })));
const CommandsPage = lazy(() =>
  import('@/pages/commands/CommandsPage').then((m) => ({ default: m.CommandsPage })));
const FleetMapPage = lazy(() =>
  import('@/pages/fleet/FleetMapPage').then((m) => ({ default: m.FleetMapPage })));
const CompliancePage = lazy(() =>
  import('@/pages/compliance/CompliancePage').then((m) => ({ default: m.CompliancePage })));
const JobsPage = lazy(() =>
  import('@/pages/jobs/JobsPage').then((m) => ({ default: m.JobsPage })));
const EnrollmentPage = lazy(() =>
  import('@/pages/enrollment/EnrollmentPage').then((m) => ({ default: m.EnrollmentPage })));
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));

dayjs.locale('es');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function PageLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
      <Spin size="large" />
    </div>
  );
}

export function App() {
  const mode = useThemeStore((s) => s.mode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const antdTheme = useMemo(() => buildAntdTheme(mode), [mode]);

  return (
    <ConfigProvider locale={esES} theme={antdTheme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Rutas publicas (login) */}
                <Route
                  element={
                    <GuestGuard>
                      <AuthLayout />
                    </GuestGuard>
                  }
                >
                  <Route path="/login" element={<LoginPage />} />
                </Route>

                {/* Rutas protegidas */}
                <Route
                  element={
                    <AuthGuard>
                      <MainLayout />
                    </AuthGuard>
                  }
                >
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/devices" element={<DevicesPage />} />
                  <Route path="/devices/:id" element={<DeviceDetailPage />} />
                  <Route path="/fleet-map" element={<FleetMapPage />} />
                  <Route path="/enrollment" element={<EnrollmentPage />} />
                  <Route path="/profiles" element={<ProfilesPage />} />
                  <Route path="/apps" element={<AppsPage />} />
                  <Route path="/commands" element={<CommandsPage />} />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route path="/admin/mdms" element={<MdmsPage />} />
                  <Route path="/admin/users" element={<UsersPage />} />
                  <Route path="/admin/enterprises" element={<EnterprisesPage />} />
                  <Route path="/admin/jobs" element={<JobsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                </Route>

                <Route path="/404" element={<NotFoundPage />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
          </HashRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
